import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";

const NAV_ITEMS = [
  { label: "Home", path: "/" },
  { label: "Skills", path: "/skills" },
  { label: "Experience", path: "/experience" },
  { label: "LeetCode", path: "/leetcode" },
  { label: "Projects", path: "/projects" },
  { label: "Open Source", path: "/opensource" },
  { label: "Contact", path: "/contact" },
];

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50);
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? window.scrollY / docHeight : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Nav $scrolled={scrolled}>
      <ProgressBar style={{ transform: `scaleX(${scrollProgress})` }} />
      <NavInner>
        <LogoLink to="/">
          <LogoGlyph>S</LogoGlyph>
          <LogoText>SHAURYA</LogoText>
        </LogoLink>
        <Hamburger
          onClick={() => setMobileOpen(!mobileOpen)}
          $open={mobileOpen}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </Hamburger>
        <NavLinks $open={mobileOpen}>
          {NAV_ITEMS.map((item) => (
            <NavLinkStyled
              key={item.path}
              to={item.path}
              $active={location.pathname === item.path}
              onClick={() => setMobileOpen(false)}
            >
              <NavLinkText data-text={item.label}>{item.label}</NavLinkText>
            </NavLinkStyled>
          ))}
        </NavLinks>
      </NavInner>
    </Nav>
  );
};

export default Navbar;

/* ── Animations ── */
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

/* ── Styled ── */
const Nav = styled.nav<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.35s ease;
  cursor: none;
  ${({ $scrolled }) =>
    $scrolled
      ? css`
          background: rgba(8, 6, 12, 0.92);
          backdrop-filter: blur(20px) saturate(1.2);
          border-bottom: 1px solid rgba(139, 92, 246, 0.06);
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);
        `
      : css`
          background: rgba(8, 6, 12, 0.4);
          backdrop-filter: blur(8px);
        `}
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #8b5cf6, #ec4899, #f59e0b);
  transform-origin: left;
  will-change: transform;
  transition: transform 0.1s linear;
`;

const NavInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.9rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  cursor: none;
`;

const LogoGlyph = styled.span`
  font-family: "Cinzel", serif;
  font-size: 1.1rem;
  font-weight: 900;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  border-radius: 6px;
  color: #fff;
  line-height: 1;
`;

const LogoText = styled.span`
  font-family: "Cinzel", serif;
  font-size: 0.85rem;
  font-weight: 700;
  color: #e0d8e8;
  letter-spacing: 0.2em;

  @media (max-width: 500px) { display: none; }
`;

const Hamburger = styled.button<{ $open: boolean }>`
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: none;
  padding: 4px;

  span {
    display: block;
    width: 24px;
    height: 2px;
    background: #b0a0c0;
    transition: all 0.3s ease;
  }

  ${({ $open }) =>
    $open &&
    css`
      span:nth-child(1) { transform: rotate(45deg) translate(5px, 5px); }
      span:nth-child(2) { opacity: 0; }
      span:nth-child(3) { transform: rotate(-45deg) translate(5px, -5px); }
    `}

  @media (max-width: 768px) { display: flex; }
`;

const NavLinks = styled.div<{ $open: boolean }>`
  display: flex;
  gap: 0.2rem;

  @media (max-width: 768px) {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(8, 6, 12, 0.98);
    backdrop-filter: blur(20px);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2.5rem;
    transform: ${({ $open }) => ($open ? "translateX(0)" : "translateX(100%)")};
    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

const NavLinkText = styled.span`
  position: relative;
  display: inline-block;
`;

const NavLinkStyled = styled(Link)<{ $active: boolean }>`
  font-family: "Raleway", sans-serif;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 0.5rem 0.8rem;
  color: ${({ $active }) => ($active ? "#e0d8e8" : "#6a5a7a")};
  text-decoration: none;
  transition: color 0.25s ease;
  cursor: none;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%) scaleX(${({ $active }) => ($active ? 1 : 0)});
    width: 16px;
    height: 2px;
    border-radius: 1px;
    background: linear-gradient(90deg, #8b5cf6, #ec4899);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover {
    color: #e0d8e8;
    &::after { transform: translateX(-50%) scaleX(1); }
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;
